import {useQueryClient} from '@tanstack/react-query';

import {
    useZoneQuery,
    zonesKeys,
} from './useZonesQuery';
import {DnsZone} from '../types';

// ⚠️ RESOLVING connectionId FOR A ROUTE THAT DOES NOT CARRY ONE.
//
// The route is `/zones/:zoneId` (registered by FNP-1186 and NOT editable by
// this ticket), but `getDnsZone(connectionId: ID!, id: ID!)` needs both — a
// zone id is only meaningful against the connection that minted it.
//
// Resolution order, cheapest and most reliable first:
//
//  1. An explicit `?connectionId=` query parameter. Every link this module
//     renders adds one, so the normal in-app path always hits this. It also
//     makes a zone's URL SHAREABLE and reload-safe, which is the property the
//     cache alone cannot give.
//  2. The list cache. Covers a link that arrived without the parameter while
//     the index page's pages are still cached — a back-navigation, a bookmark
//     followed in the same session.
//  3. Nothing. The hook stays disabled and the page says the zone cannot be
//     addressed without its connection, rather than firing a query that is
//     guaranteed to fail schema validation.
//
// A route change to `/zones/:connectionId/:zoneId` would be cleaner and is the
// honest long-term answer, but routes.tsx is shared and FNP-1186 registered it
// precisely so this ticket does not touch it. Left for a follow-up.
export const useConnectionIdFromCache = (zoneId?: string): string | undefined => {

    const queryClient = useQueryClient();

    if (!zoneId) {
        return undefined;
    }

    const cachedLists = queryClient.getQueriesData<{
        pages?: { items?: DnsZone[] }[]
    }>({queryKey: zonesKeys.lists()});

    for (const [, data] of cachedLists) {
        const match = data?.pages
                          ?.flatMap(page => page?.items ?? [])
                          ?.find(zone => zone?.id === zoneId);
        if (match?.connectionId) {
            return match.connectionId;
        }
    }

    return undefined;
};

export const useZone = (connectionId?: string, id?: string) => {

    const {
        data: record,
        isLoading,
        isError,
        isSuccess,
        refetch,
    } = useZoneQuery(connectionId, id);

    return {
        record,
        // ⚠️ A DISABLED query is not a LOADING one. Without the connectionId
        // guard, React Query reports `isLoading` true forever for a query that
        // will never run, and the page spins instead of explaining itself.
        loading: Boolean(connectionId) && isLoading,
        isError,
        // Distinct from an error: a resolved query that returned nothing means
        // the id simply does not name a zone on that connection.
        notFound: isSuccess && !record,
        // The one state the hosted-zones module has no equivalent of.
        unresolvedConnection: !connectionId,
        fetchRecord: () => refetch(),
    };
};
